// =============================================================================
// app/src/pessoal/api.ts — tudo o que fala com o BANCO PESSOAL passa por aqui.
//
// O mesmo papel que lib/consultas.ts tem para o banco da loja, e pela mesma
// razão: nenhuma tela monta query solta, então quando uma função do banco muda
// de assinatura existe UM arquivo para corrigir. Os contratos espelham
// pessoal/sql/functions (a trava) e pessoal/migrations (as tabelas); o teste
// api.test.ts confere nome de função e de parâmetro contra o SQL.
//
// QUEM NEGA É O BANCO (regra 1 e ADR-008). Toda leitura e escrita de contas e
// do guardado só passa com a sessão DESTRANCADA pela senha de 6 números — a
// RLS cobra isso. O que este arquivo acrescenta é só dizer QUE a trava fechou:
// para a RLS, sessão trancada não é erro, é "nenhuma linha", e uma tela que
// recebe zero contas desenha um planejamento vazio, que parece dado perdido.
// =============================================================================
import type { QueryClient } from '@tanstack/react-query';
import type {
  AceiteDoPortao, ComprovanteBaixado, ComprovantePessoal, ContaPessoal, EstadoDoMeuDinheiro,
  NovaContaPessoal, NovoPagamentoPessoal, PagamentoPessoal, RespostaApagarTudo, RespostaDaTrava,
  TipoDeComprovante,
} from '@pessoal/types';
import { supabase } from '../lib/supabase';
import { pessoal } from './cliente';
import type { PagamentoComComprovante } from './pagamento';

/**
 * As colunas de `receipts` que `authenticated` pode ler — TODAS as que ele
 * pode, e nenhuma a mais. O grant da 003 é POR COLUNA (`dados` e `profile_id`
 * ficam de fora), e pedir uma coluna sem grant derruba a consulta INTEIRA com
 * 42501: um `select('*')` aqui apagaria a lista de contas pagas em vez de
 * esconder o arquivo. É a mesma mordida do sale_items (18/09/2026).
 */
const COLUNAS_DO_COMPROVANTE = 'payment_id,content_type,bytes,nome,criado_em';

/**
 * A versão do texto do portão ("Quero usar"). Mudou o texto, muda a versão: o
 * aceite antigo fica gravado (consents só aceita inserção) e o portão pede um
 * novo — a LGPD quer saber QUAL texto a pessoa aceitou, não só que aceitou.
 */
export const VERSAO_DO_PORTAO = 'portao-v1';

/**
 * As chaves do TanStack Query do Meu dinheiro — TODAS debaixo de ['pessoal'].
 * É essa raiz que limparCachePessoal() apaga ao trancar e ao sair: uma chave
 * fora dela seria dado pessoal sobrevivendo na memória do aparelho do balcão.
 */
export const CHAVES_PESSOAIS = {
  /** A raiz: tudo o que é do Meu dinheiro. */
  tudo: ['pessoal'],
  estado: ['pessoal', 'estado'],
  contas: ['pessoal', 'contas'],
  guardado: ['pessoal', 'guardado'],
  aceites: ['pessoal', 'aceites'],
  pagamentos: ['pessoal', 'pagamentos'],
} as const;

/**
 * Esquece o Meu dinheiro NESTE aparelho: as consultas (e as mutações com
 * `mutationKey` debaixo de ['pessoal'], que guardam o que foi digitado).
 * Chamar ao trancar e ao sair. Não fala com o servidor.
 */
export function limparCachePessoal(qc: QueryClient): void {
  void qc.cancelQueries({ queryKey: CHAVES_PESSOAIS.tudo });
  qc.removeQueries({ queryKey: CHAVES_PESSOAIS.tudo });
  const mutacoes = qc.getMutationCache();
  for (const m of mutacoes.findAll({ mutationKey: CHAVES_PESSOAIS.tudo })) mutacoes.remove(m);
}

/**
 * A trava fechou (15 minutos parado, "Trancar" em outra aba, o dono desligou o
 * papel). Lançado no lugar do vazio que a RLS devolveria — a tela troca para o
 * cadeado em vez de mostrar um planejamento zerado. `instanceof` distingue.
 */
export class ErroTrancado extends Error {
  constructor() {
    super('O Meu dinheiro trancou. Digite sua senha de 6 números de novo.');
    this.name = 'ErroTrancado';
  }
}

/** Erro do PostgREST vira exceção — o React Query cuida do resto. */
function ou<T>(resposta: { data: T | null; error: unknown }): T {
  if (resposta.error) throw resposta.error;
  return resposta.data as T;
}

/**
 * Lança ErroTrancado se a sessão não está mais destrancada.
 *
 * Chamada DEPOIS da leitura ou da escrita que deu vazio ou erro, nunca antes: a
 * pergunta feita antes podia ouvir "aberto" e a leitura, um instante depois,
 * pegar a trava já fechada e voltar vazia. Na ordem certa, o pior caso é pedir
 * a senha de novo sem precisar — o lado seguro.
 */
async function exigirDestrancado(): Promise<void> {
  const aberto = ou<boolean | null>(await pessoal.rpc('destrancado'));
  if (aberto !== true) throw new ErroTrancado();
}

/** Uma escrita falhou: se foi porque a trava fechou, diga isso; senão, o erro original. */
async function explicarFalha(erro: unknown): Promise<never> {
  let aberto = true;
  try {
    aberto = ou<boolean | null>(await pessoal.rpc('destrancado')) === true;
  } catch {
    // Sem resposta para a segunda pergunta: fica o erro da primeira.
  }
  if (!aberto) throw new ErroTrancado();
  throw erro;
}

// --- Estado e portão ---------------------------------------------------------

/** O que a tela precisa para escolher o passo: portão, criar senha, cadeado, espera. */
export async function estado(): Promise<EstadoDoMeuDinheiro> {
  return ou<EstadoDoMeuDinheiro>(await pessoal.rpc('estado'));
}

/** O "Quero usar" do portão, com a versão do texto que a pessoa leu. */
export async function aceitarPortao(versao: string): Promise<void> {
  ou(await pessoal.from('consents').insert({ versao_texto: versao }));
}

// --- A trava (senha de 6 números) --------------------------------------------
//
// Senha errada é RESPOSTA ({ ok: false, motivo }), nunca erro (regra 12): o
// banco precisa gravar a falha. Erro aqui é só rede ou servidor. A senha vai
// no corpo da chamada e em lugar nenhum mais — nada de log, nada de guardar.

export async function criarSenha(senha: string): Promise<RespostaDaTrava> {
  return ou<RespostaDaTrava>(await pessoal.rpc('criar_senha', { p_senha: senha }));
}

export async function destrancar(senha: string): Promise<RespostaDaTrava> {
  return ou<RespostaDaTrava>(await pessoal.rpc('destrancar', { p_senha: senha }));
}

/** Estica a sessão destrancada por mais 15 minutos (até 2 horas desde que destrancou). */
export async function manterDestrancado(): Promise<RespostaDaTrava> {
  return ou<RespostaDaTrava>(await pessoal.rpc('manter_destrancado'));
}

export async function trocarSenha(atual: string, nova: string): Promise<RespostaDaTrava> {
  return ou<RespostaDaTrava>(await pessoal.rpc('trocar_senha', { p_atual: atual, p_nova: nova }));
}

/** O "esqueci a senha": exige a senha DO SISTEMA digitada há menos de 5 minutos
 *  (ver confirmarSenhaDoSistema) e mantém todos os dados. */
export async function redefinirSenha(nova: string): Promise<RespostaDaTrava> {
  return ou<RespostaDaTrava>(await pessoal.rpc('redefinir_senha', { p_nova: nova }));
}

/** Tranca a sessão deste aparelho no banco. Sair da página, 2 minutos parado, sair do sistema. */
export async function trancar(): Promise<void> {
  ou(await pessoal.rpc('trancar'));
}

/** O botão "Apagar tudo": contas, guardado, aceite, senha. A pessoa volta ao portão. */
export async function apagarTudo(): Promise<RespostaApagarTudo> {
  return ou<RespostaApagarTudo>(await pessoal.rpc('apagar_tudo'));
}

// --- As contas ---------------------------------------------------------------

/**
 * Uma linha de `items` com os números como número.
 *
 * O PostgREST costuma mandar `numeric` como número JSON, mas o tipo da coluna
 * é quem decide, e um `"1500.00"` passando por soma vira concatenação de texto
 * — o mesmo tipo de acidente da regra 9. Converter na porta custa nada.
 */
export function contaDoBanco(linha: ContaPessoal): ContaPessoal {
  const numeroOuNulo = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));
  return {
    ...linha,
    valor: Number(linha.valor),
    dia: numeroOuNulo(linha.dia),
    meses: linha.meses === null || linha.meses === undefined ? null : linha.meses.map(Number),
    parcela_no_inicio: numeroOuNulo(linha.parcela_no_inicio),
    juros_mes: numeroOuNulo(linha.juros_mes),
  };
}

/** Só as colunas que a tela escreve. O resto (id, dona, carimbos) é do banco. */
function camposDaConta(c: NovaContaPessoal): NovaContaPessoal {
  return {
    sentido: c.sentido,
    tipo: c.tipo,
    nome: c.nome,
    cor: c.cor,
    icone: c.icone,
    grupo: c.grupo,
    valor: c.valor,
    dia: c.dia,
    meses: c.meses,
    mes_inicio: c.mes_inicio,
    mes_fim: c.mes_fim,
    parcela_no_inicio: c.parcela_no_inicio,
    juros_mes: c.juros_mes,
  };
}

/** As contas de quem está logado. Lança ErroTrancado se a trava fechou. */
export async function minhasContas(): Promise<ContaPessoal[]> {
  const linhas = ou<ContaPessoal[] | null>(
    await pessoal.from('items').select('*').order('criado_em', { ascending: true }),
  ) ?? [];
  // Linha só volta com a sessão destrancada. Vazio é ambíguo — ninguém
  // cadastrou nada, ou a trava fechou e a RLS escondeu tudo —, e só então
  // vale a segunda pergunta.
  if (linhas.length === 0) await exigirDestrancado();
  return linhas.map(contaDoBanco);
}

/** Cria (sem `id`) ou altera (com `id`) uma conta, e devolve como ficou no banco. */
export async function salvarConta(conta: NovaContaPessoal & { id?: string }): Promise<ContaPessoal> {
  const campos = camposDaConta(conta);
  const resposta = conta.id
    ? await pessoal.from('items').update(campos).eq('id', conta.id).select('*').single()
    : await pessoal.from('items').insert(campos).select('*').single();
  if (resposta.error) return explicarFalha(resposta.error);
  return contaDoBanco(resposta.data as ContaPessoal);
}

export async function removerConta(id: string): Promise<void> {
  const resposta = await pessoal.from('items').delete().eq('id', id).select('id');
  if (resposta.error) return explicarFalha(resposta.error);
  // Nada apagado: ou a conta já não existia (tudo certo, era o que se queria),
  // ou a trava fechou e a RLS não deixou nem enxergar a linha.
  if ((resposta.data ?? []).length === 0) await exigirDestrancado();
}

// --- As contas pagas ---------------------------------------------------------
//
// `payments` é escrita DIRETO pelo PostgREST, como `items`: quem nega é a RLS.
// Só o BINÁRIO do comprovante passa por RPC (o arquivo não sai em `select`).

/** Uma linha de `payments` com o valor como número — o mesmo cuidado de contaDoBanco. */
export function pagamentoDoBanco(linha: PagamentoPessoal): PagamentoPessoal {
  return { ...linha, valor: Number(linha.valor) };
}

/**
 * Tudo o que já foi marcado como pago, de todos os meses, com o comprovante
 * (só os metadados) de cada um.
 *
 * DE UMA VEZ, e não um pedido por mês: as setas de mês do Resumo trocam o mês
 * em memória, e uma consulta por mês faria a tela piscar a cada toque. São
 * poucas linhas — uma por conta por mês —, e a lista inteira cabe no mesmo
 * cache que a das contas.
 *
 * O comprovante vem em SEGUNDA consulta, não em embed do PostgREST: coluna
 * errada dentro de um embed derruba a tela inteira e passa por typecheck,
 * testes e varredura (a lição do sale_items, 18/09/2026).
 */
export async function meusPagamentos(): Promise<PagamentoComComprovante[]> {
  const linhas = ou<PagamentoPessoal[] | null>(
    await pessoal.from('payments').select('*').order('competencia', { ascending: true }),
  ) ?? [];
  // Como em minhasContas: vazio é ambíguo — ninguém marcou nada, ou a trava
  // fechou e a RLS escondeu tudo. Um mês inteiro aparecendo como "falta pagar"
  // numa sessão trancada seria mentira, não tela vazia.
  if (linhas.length === 0) {
    await exigirDestrancado();
    return [];
  }
  const comprovantes = ou<ComprovantePessoal[] | null>(
    await pessoal.from('receipts').select(COLUNAS_DO_COMPROVANTE),
  ) ?? [];
  const porPagamento = new Map(comprovantes.map((c) => [c.payment_id, { ...c, bytes: Number(c.bytes) }]));
  return linhas.map((l) => ({ ...pagamentoDoBanco(l), comprovante: porPagamento.get(l.id) ?? null }));
}

/**
 * Dá o check na conta, ou corrige o que já estava marcado. Devolve como ficou.
 *
 * UPSERT por (item_id, competencia), que é a chave de negócio da 003
 * (`payments_um_por_mes`): marcar de novo a mesma conta do mesmo mês ATUALIZA
 * o valor e o dia. Assim a tela não precisa saber se já existe linha — e
 * marcar no celular uma conta que já fora marcada no computador conserta em
 * vez de estourar "já existe".
 */
export async function salvarPagamento(pagamento: NovoPagamentoPessoal): Promise<PagamentoPessoal> {
  const resposta = await pessoal.from('payments')
    .upsert({
      item_id: pagamento.item_id,
      competencia: pagamento.competencia,
      pago_em: pagamento.pago_em,
      valor: pagamento.valor,
    }, { onConflict: 'item_id,competencia' })
    .select('*').single();
  if (resposta.error) return explicarFalha(resposta.error);
  return pagamentoDoBanco(resposta.data as PagamentoPessoal);
}

/** O "desfazer": a conta volta a ser só planejada. O comprovante vai junto (o banco cascateia). */
export async function removerPagamento(id: string): Promise<void> {
  const resposta = await pessoal.from('payments').delete().eq('id', id).select('id');
  if (resposta.error) return explicarFalha(resposta.error);
  // Nada apagado: ou já não existia (era o que se queria), ou a trava fechou.
  if ((resposta.data ?? []).length === 0) await exigirDestrancado();
}

/** Anexa (ou troca) o comprovante. O arquivo vem de prepararComprovante() —
 *  foto já comprimida em JPEG, PDF como está, ambos em base64. */
export async function salvarComprovante(comprovante: {
  pagamentoId: string; base64: string; contentType: TipoDeComprovante; nome: string;
}): Promise<void> {
  const resposta = await pessoal.rpc('salvar_comprovante', {
    p_payment_id: comprovante.pagamentoId,
    p_dados: comprovante.base64,
    p_content_type: comprovante.contentType,
    p_nome: comprovante.nome,
  });
  if (resposta.error) return explicarFalha(resposta.error);
}

/**
 * O arquivo em si, para abrir numa aba. Só sai por aqui.
 *
 * `null` NÃO é erro: o banco expurga o comprovante com mais de 12 meses e
 * mantém o pagamento, então "o arquivo não está mais aqui" é um caso normal
 * desta tela. Quem chama mostra o recado; estourar uma exceção seria dizer
 * que algo quebrou quando nada quebrou.
 */
export async function obterComprovante(pagamentoId: string): Promise<ComprovanteBaixado | null> {
  const resposta = await pessoal.rpc('obter_comprovante', { p_payment_id: pagamentoId });
  if (resposta.error) return explicarFalha(resposta.error);
  return (resposta.data as ComprovanteBaixado | null) ?? null;
}

/** Tira o comprovante e deixa a conta marcada. Apaga o arquivo do banco. */
export async function removerComprovante(pagamentoId: string): Promise<void> {
  const resposta = await pessoal.from('receipts').delete().eq('payment_id', pagamentoId).select('payment_id');
  if (resposta.error) return explicarFalha(resposta.error);
  if ((resposta.data ?? []).length === 0) await exigirDestrancado();
}

// --- O guardado (a reserva) --------------------------------------------------

/** Quanto a pessoa diz ter guardado hoje. 0 quando nunca respondeu. */
export async function meuGuardado(): Promise<number> {
  const linha = ou<{ valor: number | string } | null>(
    await pessoal.from('savings').select('valor').maybeSingle(),
  );
  if (linha === null) {
    await exigirDestrancado();
    return 0;
  }
  return Number(linha.valor);
}

/** Grava quanto a pessoa tem guardado (uma linha por pessoa: insere ou troca). */
export async function salvarGuardado(valor: number): Promise<void> {
  // O número vem de paraNumero() na tela (regra 9). Aqui, só a última porta:
  // NaN ou negativo não viajam.
  if (!Number.isFinite(valor) || valor < 0) {
    throw new Error('Valor guardado inválido: use um número de zero para cima.');
  }
  const resposta = await pessoal.from('savings').upsert({ valor }, { onConflict: 'profile_id' });
  if (resposta.error) return explicarFalha(resposta.error);
}

// --- Baixar os meus dados ----------------------------------------------------

/** O aceite do portão (e os anteriores, se o texto mudou de versão). */
async function meusAceites(): Promise<AceiteDoPortao[]> {
  return ou<AceiteDoPortao[] | null>(
    await pessoal.from('consents').select('*').order('aceito_em', { ascending: true }),
  ) ?? [];
}

/**
 * Tudo o que o banco guarda da pessoa, para ela baixar (LGPD).
 *
 * Os pagamentos entram desde a 003 — "esta conta eu paguei, neste dia, este
 * valor" é dado dela como qualquer outro, e um arquivo que não os levasse
 * seria uma cópia pela metade. Do comprovante vão só os METADADOS (tipo,
 * tamanho, nome, quando): o arquivo em si sai um a um pela RPC, e enfiar
 * megabytes de base64 dentro do JSON faria o botão travar o celular no meio
 * do balcão. O que se perde é recuperável — o arquivo continua no banco, a um
 * toque de "comprovante" em Contas.
 */
export async function baixarMeusDados(): Promise<{
  contas: ContaPessoal[];
  guardado: number;
  aceites: AceiteDoPortao[];
  pagamentos: PagamentoComComprovante[];
  gerado_em: string;
}> {
  const [contas, guardado, aceites, pagamentos] = await Promise.all([
    minhasContas(), meuGuardado(), meusAceites(), meusPagamentos(),
  ]);
  return { contas, guardado, aceites, pagamentos, gerado_em: new Date().toISOString() };
}

// --- A senha do SISTEMA, digitada de novo ------------------------------------

/**
 * Confere a senha do sistema entrando de novo pelo cliente PRINCIPAL, com o
 * e-mail de quem está logado.
 *
 * É assim que o banco pessoal sabe que a pessoa "acabou de digitar a senha do
 * sistema": o login novo põe no token um `amr` com a hora de agora, e
 * criar_senha, redefinir_senha e o destrancar depois de 10 erros exigem essa
 * hora com menos de 5 minutos. Um colega com a sessão aberta no balcão não
 * passa daqui sem saber a senha da dona da conta.
 *
 * O login novo é uma SESSÃO nova (outro session_id): a trava de uma sessão
 * não vale para a outra, e é por isso que esta confirmação vem antes de
 * criar/redefinir a senha, que já destrancam a sessão nova.
 *
 * `false` é senha errada. Qualquer outra falha (rede, limite de tentativas)
 * é erro — "senha errada" seria mentira nesses casos. A senha não é
 * registrada nem guardada em lugar nenhum.
 */
export async function confirmarSenhaDoSistema(senha: string): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  const email = data.session?.user.email;
  if (!email) throw new Error('Sessão expirada — entre de novo.');
  const { error } = await supabase.auth.signInWithPassword({ email, password: senha });
  if (!error) return true;
  const e = error as { code?: string; status?: number; message?: string };
  if (e.code === 'invalid_credentials' || /Invalid login credentials/i.test(e.message ?? '')) return false;
  throw error;
}
